import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ProviderSelect } from '../components/ProviderSelect'
import { providerLabel, useProviderCatalog } from '../lib/providers'
import Sidebar from '../components/Sidebar'
import { api, ApiRequestError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

const BACKEND = import.meta.env.VITE_API_URL ?? ''

type Tab = 'profile' | 'apikey' | 'data'

// Parse the filename from a Content-Disposition header (the backend already
// stamps the export name with a datetime). Falls back to a client-side
// timestamp so repeated downloads never overwrite each other.
function filenameFromContentDisposition(header: string | null): string {
  const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)
  if (match?.[1]) return decodeURIComponent(match[1])
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '').replace(/(\d{8})(\d{6})/, '$1-$2')
  return `proof-of-work-export-${ts}.zip`
}

// ---------------------------------------------------------------------------
// Profile tab
// ---------------------------------------------------------------------------

const FILENAME_FORMAT_OPTIONS = [
  { value: '{firstname}_{lastname}-{company}', label: 'Firstname_Lastname-Company (default)', example: 'Yosef_Sukenik-Acme.docx' },
  { value: '{lastname}_{firstname}-{company}', label: 'Lastname_Firstname-Company',           example: 'Sukenik_Yosef-Acme.docx' },
  { value: '{firstname}_{lastname}',           label: 'Firstname_Lastname (no company)',       example: 'Yosef_Sukenik.docx' },
  { value: '{company}-{firstname}_{lastname}', label: 'Company-Firstname_Lastname',            example: 'Acme-Yosef_Sukenik.docx' },
  { value: '{firstname}-{company}',            label: 'Firstname-Company',                     example: 'Yosef-Acme.docx' },
]
const DEFAULT_FILENAME_FORMAT = FILENAME_FORMAT_OPTIONS[0].value

interface EducationEntry {
  institution: string
  degree?: string
  field?: string
  year?: string
}

interface ProfileData {
  full_name: string
  email: string
  phone: string
  linkedin_url: string
  educationText: string
  languagesText: string
  cv_filename_format: string
}

function educationToText(entries: EducationEntry[]): string {
  return entries
    .map((e) => [e.degree, e.institution, e.year].filter(Boolean).join(', '))
    .join('\n')
}

function textToEducation(text: string): EducationEntry[] {
  const isYear = (s: string) => /^\d{4}$/.test(s)
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim()).filter(Boolean)
      if (parts.length >= 3) return { degree: parts[0], institution: parts[1], year: parts[2] }
      if (parts.length === 2) {
        if (isYear(parts[1])) return { institution: parts[0], year: parts[1] }
        return { degree: parts[0], institution: parts[1] }
      }
      return { institution: parts[0] ?? line }
    })
}

function textToLanguages(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean)
}

function ProfileTab() {
  const [form, setForm] = useState<ProfileData>({
    full_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    educationText: '',
    languagesText: '',
    cv_filename_format: DEFAULT_FILENAME_FORMAT,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{
        full_name: string
        email: string
        phone: string | null
        linkedin_url: string | null
        education: EducationEntry[] | null
        languages: string[] | null
        preferences: Record<string, string> | null
      }>('/api/v1/user/profile')
      .then((data) => {
        setForm({
          full_name: data.full_name ?? '',
          email: data.email ?? '',
          phone: data.phone ?? '',
          linkedin_url: data.linkedin_url ?? '',
          educationText: educationToText(data.education ?? []),
          languagesText: (data.languages ?? []).join('\n'),
          cv_filename_format: data.preferences?.cv_filename_format || DEFAULT_FILENAME_FORMAT,
        })
      })
      .catch(() => setError('Could not load profile'))
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      await api.put('/api/v1/user/profile', {
        full_name: form.full_name || null,
        phone: form.phone || null,
        linkedin_url: form.linkedin_url || null,
        education: textToEducation(form.educationText),
        languages: textToLanguages(form.languagesText),
        preferences: { cv_filename_format: form.cv_filename_format },
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <span className="animate-spin w-5 h-5 border-2 border-primary-container/30 border-t-primary-container rounded-full inline-block" />
      </div>
    )
  }

  return (
    <section className="bg-surface-container-lowest rounded-2xl border border-outline-variant/10 shadow-sm overflow-hidden max-w-2xl">
      <div className="p-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="material-symbols-outlined text-primary-container">person</span>
          <div>
            <h3 className="text-xl font-headline font-semibold text-on-surface">Basic information</h3>
            <p className="text-sm text-secondary mt-0.5">This is what will be used in your generated CVs</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-error-container/20 border-l-4 border-error rounded text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="space-y-5">
          <Field label="Full name" id="full_name">
            <input
              id="full_name"
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className={inputCls}
            />
          </Field>

          <Field label="Phone number" id="phone">
            <input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 (555) 000-0000"
              className={inputCls}
            />
          </Field>

          <Field label="Email address" id="email">
            <div className="relative">
              <input
                id="email"
                type="email"
                value={form.email}
                readOnly
                className={`${inputCls} pl-10 bg-surface-container-low text-secondary cursor-not-allowed`}
              />
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-secondary/60 text-[18px]">
                lock
              </span>
            </div>
            <p className="text-xs text-secondary/60 mt-1">Managed by your Google account</p>
          </Field>

          <Field label="LinkedIn URL" id="linkedin" optional>
            <input
              id="linkedin"
              type="url"
              value={form.linkedin_url}
              onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
              placeholder="linkedin.com/in/username"
              className={inputCls}
            />
          </Field>

          <Field label="Education" id="education" optional>
            <textarea
              id="education"
              value={form.educationText}
              onChange={(e) => setForm({ ...form, educationText: e.target.value })}
              placeholder={'BSc Computer Science, University of Example, 2015\nMBA, Business School, 2018'}
              rows={3}
              className={`${inputCls} resize-none`}
            />
            <p className="text-xs text-secondary/60 mt-1">One entry per line: Degree, Institution, Year</p>
          </Field>

          <Field label="Languages" id="languages" optional>
            <textarea
              id="languages"
              value={form.languagesText}
              onChange={(e) => setForm({ ...form, languagesText: e.target.value })}
              placeholder={'English (native)\nHebrew (fluent)'}
              rows={3}
              className={`${inputCls} resize-none`}
            />
            <p className="text-xs text-secondary/60 mt-1">One language per line</p>
          </Field>
        </div>

        <div className="pt-6 border-t border-outline-variant/10 mt-6">
          <div className="flex items-center gap-2 mb-5">
            <span className="material-symbols-outlined text-primary-container text-[18px]">download</span>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-secondary">Document preferences</h4>
          </div>
          <Field label="CV filename format" id="cv_filename_format" optional>
            <select
              id="cv_filename_format"
              value={form.cv_filename_format}
              onChange={(e) => setForm({ ...form, cv_filename_format: e.target.value })}
              className={inputCls}
            >
              {FILENAME_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-secondary/60 mt-1">
              Example:{' '}
              <span className="font-mono">
                {FILENAME_FORMAT_OPTIONS.find((o) => o.value === form.cv_filename_format)?.example ?? FILENAME_FORMAT_OPTIONS[0].example}
              </span>
            </p>
          </Field>
        </div>
      </div>

      <div className="px-8 py-5 bg-surface-container-low/30 border-t border-outline-variant/10 flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-2.5 bg-primary-container text-on-primary rounded-xl text-sm font-semibold hover:bg-primary transition-all duration-200 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && (
          <span className="text-sm text-green-700 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-base">check_circle</span>
            Saved
          </span>
        )}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// API Key tab
// ---------------------------------------------------------------------------

type VerifyState = 'idle' | 'verifying' | 'valid' | 'invalid'

function ApiKeyTab() {
  const { user, fetchUser } = useAuthStore()
  const [mode, setMode] = useState<'managed' | 'byo'>(
    user?.apiKeyMode === 'byo' ? 'byo' : 'managed',
  )
  const { providers, currentProvider } = useProviderCatalog()
  const [provider, setProvider] = useState('anthropic')
  const [rawKey, setRawKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [verifyState, setVerifyState] = useState<VerifyState>('idle')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (currentProvider) setProvider(currentProvider)
  }, [currentProvider])

  const handleVerify = async () => {
    if (!rawKey.trim()) return
    setVerifyState('verifying')
    setError(null)
    try {
      const res = await api.post<{ valid: boolean; message: string }>('/api/v1/user/apikey/verify', {
        raw_key: rawKey,
        provider,
      })
      setVerifyState(res.valid ? 'valid' : 'invalid')
      if (!res.valid) setError(res.message)
    } catch {
      setVerifyState('invalid')
      setError('Verification failed')
    }
  }

  const handleSave = async () => {
    if (verifyState !== 'valid') return
    setSaving(true)
    setError(null)
    try {
      await api.put('/api/v1/user/apikey', { raw_key: rawKey, provider })
      await fetchUser()
      setRawKey('')
      setVerifyState('idle')
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveKey = async () => {
    try {
      await api.delete('/api/v1/user/apikey')
      setMode('managed')
      await fetchUser()
    } catch {
      setError('Could not remove key')
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* Mode selector cards */}
      <div className="grid grid-cols-2 gap-4">
        {/* Managed key card */}
        <button
          onClick={() => setMode('managed')}
          className={[
            'flex flex-col p-6 rounded-xl border-2 text-left transition-all',
            mode === 'managed'
              ? 'border-primary-container shadow-sm'
              : 'border-outline-variant/30 hover:border-primary-container/50',
          ].join(' ')}
        >
          <div className="flex justify-between items-start mb-4">
            <div className="bg-primary-container/10 p-2 rounded-lg text-primary-container">
              <span className="material-symbols-outlined">hub</span>
            </div>
            {mode === 'managed' && (
              <span
                className="material-symbols-outlined text-primary-container"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                check_circle
              </span>
            )}
          </div>
          <h3 className="font-semibold text-on-surface mb-1">Shared managed key</h3>
          <p className="text-xs text-secondary leading-relaxed mb-4">
            Uses the system's shared key. Subject to a monthly usage cap.
          </p>
          <div className="mt-auto pt-4 border-t border-outline-variant/10 flex items-center gap-1.5 text-primary-container">
            <span className="material-symbols-outlined text-sm">hourglass_top</span>
            <span className="text-[11px] font-bold uppercase tracking-wider">Monthly cap active</span>
          </div>
        </button>

        {/* BYO key card */}
        <button
          onClick={() => setMode('byo')}
          className={[
            'flex flex-col p-6 rounded-xl border-2 text-left transition-all',
            mode === 'byo'
              ? 'border-primary-container shadow-sm'
              : 'border-outline-variant/30 hover:border-primary-container/50',
          ].join(' ')}
        >
          <div className="flex justify-between items-start mb-4">
            <div className="bg-on-surface/5 p-2 rounded-lg text-on-surface">
              <span className="material-symbols-outlined">key</span>
            </div>
            {mode === 'byo' && (
              <span
                className="material-symbols-outlined text-primary-container"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                check_circle
              </span>
            )}
          </div>
          <h3 className="font-semibold text-on-surface mb-1">Your own API key</h3>
          <p className="text-xs text-secondary leading-relaxed mb-4">
            Direct integration with Anthropic. No usage caps.
          </p>
          <div className="mt-auto pt-4 border-t border-outline-variant/10 flex items-center gap-1.5 text-green-700">
            <span className="material-symbols-outlined text-sm">bolt</span>
            <span className="text-[11px] font-bold uppercase tracking-wider">No usage caps</span>
          </div>
        </button>
      </div>

      {/* Key input (shown when BYO selected) */}
      {mode === 'byo' && (
        <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/20 shadow-sm p-8 space-y-5">
          {error && (
            <div className="p-3 bg-error-container/20 border-l-4 border-error rounded text-sm text-red-800">
              {error}
            </div>
          )}

          <Field label="AI provider" id="apikey-provider">
            <ProviderSelect
              id="apikey-provider"
              value={provider}
              onChange={(p) => {
                setProvider(p)
                setVerifyState('idle')
              }}
              providers={providers}
              className={inputCls}
            />
          </Field>

          <Field label={`Your ${providerLabel(providers, provider)} API key`} id="apikey">
            <div className="relative flex items-center">
              <input
                id="apikey"
                type={showKey ? 'text' : 'password'}
                value={rawKey}
                onChange={(e) => {
                  setRawKey(e.target.value)
                  setVerifyState('idle')
                }}
                placeholder="sk-ant-..."
                className={`${inputCls} pr-10 font-mono`}
              />
              <button
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 text-secondary hover:text-on-surface transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">
                  {showKey ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
          </Field>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleVerify}
              disabled={!rawKey.trim() || verifyState === 'verifying'}
              className="px-6 py-2.5 border border-on-surface text-on-surface text-sm font-semibold rounded-xl hover:bg-surface-container-low transition-all disabled:opacity-40"
            >
              {verifyState === 'verifying' ? 'Verifying…' : 'Verify'}
            </button>

            {verifyState === 'valid' && (
              <>
                <span className="flex items-center gap-1.5 text-green-700 bg-green-50 px-3 py-1.5 rounded-full border border-green-100 text-[11px] font-bold uppercase tracking-wide">
                  <span
                    className="material-symbols-outlined text-base"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    check_circle
                  </span>
                  Key verified
                </span>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-8 py-2.5 bg-primary-container text-on-primary rounded-xl text-sm font-semibold hover:bg-primary transition-all disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save key'}
                </button>
              </>
            )}

            {verifyState === 'invalid' && (
              <span className="flex items-center gap-1.5 text-red-700 text-sm">
                <span className="material-symbols-outlined text-base">error</span>
                Invalid key — check and try again
              </span>
            )}
          </div>

          <p className="text-xs text-secondary flex items-center gap-1.5 pt-2 border-t border-outline-variant/10">
            <span className="material-symbols-outlined text-base">lock</span>
            Your key is encrypted at rest and never included in API responses.
          </p>

          {user?.apiKeyMode === 'byo' && (
            <button
              onClick={handleRemoveKey}
              className="text-xs text-secondary hover:text-error transition-colors"
            >
              Remove key and revert to managed
            </button>
          )}
        </section>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Data tab
// ---------------------------------------------------------------------------

function DataTab() {
  const navigate = useNavigate()
  const { signOut, user } = useAuthStore()
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // ===========================================================================
  // TEMPORARY: admin-only re-trigger onboarding state.
  // REMOVE THIS BLOCK (state + handler + JSX section below marked TEMPORARY)
  // BEFORE THE SYSTEM IS MADE PUBLIC.
  // ===========================================================================
  const [resetting, setResetting] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)

  const handleResetOnboarding = async () => {
    if (!window.confirm(
      'Reset onboarding and clear your profile?\n\n' +
      'Your library, embeddings, and existing CVs are kept. Your profile fields ' +
      '(name, email, phone, education, languages) will be cleared and you will ' +
      'be sent through onboarding again on your next login.'
    )) {
      return
    }
    setResetting(true)
    setResetError(null)
    try {
      await api.post('/api/v1/onboarding/v2/admin/reset')
      // Sign out so the next login routes through onboarding cleanly.
      await signOut()
      navigate('/')
    } catch (err) {
      setResetError((err as Error).message)
    } finally {
      setResetting(false)
    }
  }
  // ===========================================================================
  // END TEMPORARY
  // ===========================================================================

  const handleDownload = async () => {
    setDownloading(true)
    setDownloadError(null)
    try {
      const res = await fetch(`${BACKEND}/api/v1/user/data/export`, { credentials: 'include' })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Honour the server's timestamped filename (Content-Disposition); fall back
      // to a client-side timestamp so repeated exports never collide.
      a.download = filenameFromContentDisposition(res.headers.get('content-disposition'))
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setDownloadError('Export failed. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await api.delete('/api/v1/user/account')
      await signOut()
      navigate('/', { replace: true })
    } catch (err) {
      setDeleteError(err instanceof ApiRequestError ? err.error.message : 'Deletion failed')
      setDeleting(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-10">
      {/* Download */}
      <section className="bg-surface-container-lowest rounded-2xl border border-outline-variant/10 shadow-sm p-8">
        <h3 className="text-xl font-headline font-semibold text-on-surface mb-2">
          Download your data
        </h3>
        <p className="text-sm text-secondary mb-6">
          Export a copy of your library, CVs, and profile as a ZIP file.
        </p>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="px-6 py-2.5 border border-on-surface text-on-surface text-sm font-semibold rounded-xl hover:bg-surface-container-low transition-all disabled:opacity-50"
        >
          {downloading ? 'Preparing…' : 'Download my data'}
        </button>
        {downloadError && (
          <p className="mt-3 text-sm text-error">{downloadError}</p>
        )}
      </section>

      {/* =====================================================================
            TEMPORARY: admin-only re-trigger onboarding.
            REMOVE THIS <section> BEFORE THE SYSTEM IS MADE PUBLIC.
            See backend/api/onboarding.py for the matching endpoint comment.
           ===================================================================== */}
      {user?.isAdmin && (
        <section className="bg-surface-container-lowest rounded-2xl border border-outline-variant/10 shadow-sm p-8">
          <h3 className="text-xl font-headline font-semibold text-on-surface mb-2">
            Re-run onboarding (admin)
          </h3>
          <p className="text-sm text-secondary mb-6">
            Clears your profile and resets onboarding so you can test the prefill flow.
            Your library, embeddings, and existing CVs are kept. You will be signed out.
          </p>
          <button
            onClick={handleResetOnboarding}
            disabled={resetting}
            className="px-6 py-2.5 border border-outline-variant text-on-surface text-sm font-semibold rounded-xl hover:bg-surface-container-low transition-all disabled:opacity-50"
          >
            {resetting ? 'Resetting…' : 'Re-run onboarding'}
          </button>
          {resetError && (
            <p className="mt-3 text-sm text-error">{resetError}</p>
          )}
        </section>
      )}
      {/* ===================================================================== END TEMPORARY */}

      {/* Delete account */}
      <section className="bg-surface-container-lowest rounded-2xl border border-outline-variant/10 shadow-sm p-8">
        <h3 className="text-xl font-headline font-semibold text-on-surface mb-2">
          Delete account
        </h3>
        <p className="text-sm text-secondary mb-6">
          Permanently delete your account and all associated data. This cannot be undone.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="px-6 py-2.5 border border-outline-variant text-red-800 text-sm font-semibold rounded-xl hover:bg-red-50 transition-all"
        >
          Delete my account
        </button>
      </section>

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/20 backdrop-blur-sm">
          <div className="w-full max-w-[480px] bg-surface-container-lowest rounded-xl shadow-[0_20px_40px_rgba(26,28,26,0.12)] overflow-hidden">
            <div className="px-8 py-6 flex items-center justify-between">
              <h3 className="text-xl font-headline text-on-surface">Delete your account?</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setConfirmText('')
                  setDeleteError(null)
                }}
                className="material-symbols-outlined text-secondary hover:text-on-surface transition-colors"
              >
                close
              </button>
            </div>

            <div className="px-8 pb-8 space-y-5">
              <div className="p-4 bg-error-container/20 rounded-lg border-l-4 border-error">
                <p className="text-sm text-red-800 leading-relaxed font-medium">
                  This will permanently delete your library, all proofs, all generated CVs, and
                  your account. This action cannot be undone.
                </p>
              </div>

              {deleteError && (
                <p className="text-sm text-error">{deleteError}</p>
              )}

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-secondary mb-2">
                  To confirm, type{' '}
                  <span className="text-on-surface">DELETE</span> in the box below.
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="w-full px-4 py-3 bg-surface-container-low border-b-2 border-outline-variant focus:border-primary-container outline-none text-on-surface transition-colors"
                />
              </div>
            </div>

            <div className="px-8 py-5 bg-surface-container-low flex flex-row-reverse gap-3">
              <button
                onClick={handleDelete}
                disabled={confirmText !== 'DELETE' || deleting}
                className="px-6 py-2.5 bg-[#7F1D1D] text-white text-sm font-bold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:bg-red-900"
              >
                {deleting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setConfirmText('')
                  setDeleteError(null)
                }}
                className="px-6 py-2.5 bg-surface-container-lowest border border-on-surface text-on-surface text-sm font-bold rounded-xl hover:bg-surface-container-low transition-all"
              >
                No, don't delete my account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const inputCls =
  'w-full px-4 py-3 bg-surface-bright border border-outline-variant/40 rounded-xl focus:ring-1 focus:ring-primary-container focus:border-primary-container transition-all outline-none text-on-surface'

function Field({
  label,
  id,
  optional,
  children,
}: {
  label: string
  id: string
  optional?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-secondary">
          {label}
        </label>
        {optional && <span className="text-[10px] text-secondary/60 font-medium">OPTIONAL</span>}
      </div>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Settings page shell
// ---------------------------------------------------------------------------

const TABS: { key: Tab; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'apikey', label: 'API Key' },
  { key: 'data', label: 'Data' },
]

const TAB_KEYS: Tab[] = ['profile', 'apikey', 'data']

export default function SettingsPage() {
  const [searchParams] = useSearchParams()
  // Deep-link support: /settings?tab=apikey (the quota gate's "Use your own API key" CTA).
  const requestedTab = searchParams.get('tab')
  const initialTab: Tab = TAB_KEYS.includes(requestedTab as Tab)
    ? (requestedTab as Tab)
    : 'profile'
  const [activeTab, setActiveTab] = useState<Tab>(initialTab)

  return (
    <div className="min-h-screen bg-surface-container-low flex">
      <Sidebar />

      <main className="flex-1 ml-16 lg:ml-64 min-h-screen">
        <div className="max-w-6xl mx-auto px-12 py-12">
          {/* Header */}
          <header className="mb-10">
            <h2 className="text-4xl font-headline font-semibold text-on-surface mb-8">Settings</h2>
            <nav className="flex items-center gap-8 border-b border-outline-variant/30">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'pb-4 text-sm font-medium transition-colors',
                    activeTab === tab.key
                      ? 'text-primary border-b-2 border-primary-container'
                      : 'text-secondary hover:text-on-surface',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </header>

          {/* Tab content */}
          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'apikey' && <ApiKeyTab />}
          {activeTab === 'data' && <DataTab />}
        </div>
      </main>
    </div>
  )
}
